import { useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import Spinner from "../../Components/Spinner";
import ErrorMessage from "../../Components/ErrorMessage";
import MandatorySign from "../../Components/MandatorySign";
import { useLanguage } from "../../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const TIMEZONES = [
    "Europe/Rome",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Madrid",
    "Europe/Lisbon",
    "Europe/Amsterdam",
    "Europe/Athens",
    "Europe/Istanbul",
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Toronto",
    "America/Sao_Paulo",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
];

const TimeDropdown = ({
    value,
    options,
    placeholder,
    onChange,
    disabled,
}) => (
    <Dropdown>
        <Dropdown.Toggle
            as="button"
            type="button"
            className="register_form_first fade-in time_dropdown_toggle"
            disabled={disabled}
        >
            {value || placeholder}
        </Dropdown.Toggle>

        <Dropdown.Menu className="custom_dropdown_menu time_dropdown_menu">
            <div className="scrollable_dropdown_cont">
                {options.map((option) => (
                    <Dropdown.Item
                        key={option}
                        active={value === option}
                        onClick={() => onChange(option)}
                    >
                        {option}
                    </Dropdown.Item>
                ))}
            </div>
        </Dropdown.Menu>
    </Dropdown>
);

const GeoSettingsModal = ({
    account_id,
    org_id,
    geo_id,
    show,
    handleClose,
    fetchGeoSettings,
    geoSettings,
}) => {
    const { t } = useLanguage();
    const [loadingSave, setLoadingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        message: "",
        display: false,
    });

    const [formData, setFormData] = useState({
        interval_value: geoSettings?.interval_value ?? 3,
        interval_unit: geoSettings?.interval_unit ?? "w",
        interval_time: geoSettings?.interval_time ?? "",
        interval_timezone: geoSettings?.interval_timezone ?? "Europe/Rome",
    })

    const INTERVAL_UNITS = {
        d: {
            singular: t.geo.day,
            plural: t.geo.days,
        },
        w: {
            singular: t.geo.week,
            plural: t.geo.weeks,
        }
        // mo: {
        //     singular: t.geo.month,
        //     plural: t.geo.months,
        // },
    };

    const handleIntervalChange = (e) => {
        const { value } = e.target;

        setFormData((prev) => ({
            ...prev,
            interval_value: value,
        }));
    };

    const handleUnitChange = (unit) => {
        setFormData((prev) => ({
            ...prev,
            interval_unit: unit,
        }));
    };

    const handleTimeChange = (time) => {
        setFormData((prev) => ({
            ...prev,
            interval_time: time,
        }));
    };

    const handleSave = async () => {
        const interval = Number(formData.interval_value);
        const maxInterval = formData.interval_unit === "w" ? 4 : 30;

        if (
            formData.interval_value === "" ||
            !Number.isInteger(interval) ||
            interval < 1 ||
            interval > maxInterval
        ) {
            setErrorMessage({
                message: `${t.geo.interval_err} ${maxInterval} ${formData.interval_unit === "w" ? t.geo.weeks : t.geo.days
                    }.`,
                display: true,
            });
            return;
        }

        if (!formData.interval_time) {
            setErrorMessage({
                message: t.geo.time_err,
                display: true,
            });
            return;
        }

        setLoadingSave(true);

        setErrorMessage({
            message: "",
            display: false,
        });

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/geo_settings`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id,
                        interval_value: Number(formData.interval_value),
                        interval_unit: formData.interval_unit,
                        interval_time: formData.interval_time,
                        interval_timezone: formData.interval_timezone,
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error || "Something went wrong."
                );
            }

            if (data.message === "OK") {
                handleClose();
                fetchGeoSettings()
            } else {
                throw new Error(
                    data.message || "Unable to save the schedule."
                );
            }
        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error.",
                display: true,
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const closeModal = () => {
        if (loadingSave) return;

        handleClose();

        setFormData({
            interval_value: geoSettings?.interval_value ?? 3,
            interval_unit: geoSettings?.interval_unit ?? "w",
            interval_time: geoSettings?.interval_time ?? "",
            interval_timezone: geoSettings?.interval_timezone ?? "Europe/Rome"
        });

        setErrorMessage({
            message: "",
            display: false,
        });
    };

    const selectedUnit = INTERVAL_UNITS[formData.interval_unit];

    const intervalLabel =
        Number(formData.interval_value) === 1
            ? selectedUnit.singular
            : selectedUnit.plural;

    return (
        <Modal
            show={show}
            centered
            onHide={closeModal}
        >
            <div className="modal_header">
                <p className="modal_header_title">
                    {t.geo.schedule_settings_title}
                </p>
            </div>

            <div className="modal_main_cont">
                <div className="modal_profile_input_cont">
                    <div className="label_input">
                        <label className="input_label">
                            {t.geo.every}{" "}
                            <MandatorySign
                                message={t.mandatory_field}
                            />
                        </label>

                        <div className="label_input_dis_cont">
                            <input
                                type="number"
                                name="interval_value"
                                min="1"
                                max="31"
                                step="1"
                                value={formData.interval_value}
                                onChange={handleIntervalChange}
                                className="register_form_first fade-in"
                                placeholder="3"
                                disabled={loadingSave}
                            />

                            <Dropdown>
                                <Dropdown.Toggle
                                    as="div"
                                    className="register_form_first fade-in"
                                    style={{
                                        minWidth: "120px",
                                        cursor: loadingSave ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {Number(formData.interval_value) === 1
                                        ? INTERVAL_UNITS[formData.interval_unit].singular
                                        : INTERVAL_UNITS[formData.interval_unit].plural}
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="custom_dropdown_menu">
                                    {Object.entries(INTERVAL_UNITS).map(([unit, labels]) => (
                                        <Dropdown.Item
                                            key={unit}
                                            disabled={loadingSave}
                                            active={formData.interval_unit === unit}
                                            onClick={() => handleUnitChange(unit)}
                                        >
                                            {Number(formData.interval_value) === 1
                                                ? labels.singular
                                                : labels.plural}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        <div className="label_input">
                            <label className="input_label">
                                {t.geo.at} <MandatorySign message={t.mandatory_field} />
                            </label>

                            <div className="label_input_dis_cont">
                                <div className="time_picker">
                                    <TimeDropdown
                                        value={formData.interval_time?.split(":")[0] || ""}
                                        placeholder="HH"
                                        options={Array.from(
                                            { length: 24 },
                                            (_, i) => String(i).padStart(2, "0")
                                        )}
                                        disabled={loadingSave}
                                        onChange={(hour) => {
                                            const minute =
                                                formData.interval_time?.split(":")[1] || "00";

                                            handleTimeChange(`${hour}:${minute}`);
                                        }}
                                    />

                                    <span className="time_picker_separator">:</span>

                                    <TimeDropdown
                                        value={formData.interval_time?.split(":")[1] || ""}
                                        placeholder="MM"
                                        options={Array.from(
                                            { length: 60 },
                                            (_, i) => String(i).padStart(2, "0")
                                        )}
                                        disabled={loadingSave}
                                        onChange={(minute) => {
                                            const hour =
                                                formData.interval_time?.split(":")[0] || "00";

                                            handleTimeChange(`${hour}:${minute}`);
                                        }}
                                    />
                                </div>

                                <Dropdown>
                                    <Dropdown.Toggle
                                        as="button"
                                        type="button"
                                        className="register_form_first fade-in w-100"
                                        disabled={loadingSave}
                                    >
                                        {formData.interval_timezone || "Select timezone"}
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="custom_dropdown_menu">
                                        <div className="scrollable_dropdown_cont">

                                            {TIMEZONES.map((timezone) => (
                                                <Dropdown.Item
                                                    key={timezone}
                                                    active={formData.interval_timezone === timezone}
                                                    disabled={loadingSave}
                                                    onClick={() => {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            interval_timezone: timezone,
                                                        }));
                                                    }}
                                                >
                                                    {timezone}
                                                </Dropdown.Item>
                                            ))}
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>

                            </div>
                        </div>


                        <p className="warning_source_message fade-in">
                            {t.geo.run} {formData.interval_value || "…"} {intervalLabel} {t.geo.at} {formData.interval_time} - {formData.interval_timezone}.
                            <br/>
                            {t.geo.schedule_warn}
                        </p>
                    </div>
                </div>
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        type="button"
                        className="modale_save_btn"
                        onClick={handleSave}
                        disabled={loadingSave}
                    >
                        {loadingSave ? (
                            <Spinner loading={true} />
                        ) : (
                            t.save
                        )}
                    </button>

                    <button
                        type="button"
                        className="modale_close_btn"
                        onClick={closeModal}
                        disabled={loadingSave}
                    >
                        {t.close}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default GeoSettingsModal;